import { useEffect, useState } from 'react'
import { alpha, Typography, Divider } from '@mui/material'
import { v4 as uuidv4 } from 'uuid'
import colors from 'styles/colors'
import { getDisplayScore } from 'utils/formatText'
import { useTranslation } from 'react-i18next'
import { ColStack } from 'components/atoms/container/InCardStack'
import RowStack from 'components/atoms/container/RowStack'
import { AverageComparisionCard, ScoreBarWrapper } from './BehaviourMolecules'

const BehaviourComparison = ({
  currentAssessment,
  prevAssessment,
  behaviour
}) => {
  const { t } = useTranslation()
  const [currentBehaviour, setCurrentBehaviour] = useState(null)
  const [prevBehaviour, setPrevBehaviour] = useState(null)
  const [displayData, setDisplayData] = useState({})

  useEffect(() => {
    if (behaviour) {
      const newData = currentAssessment?.behaviourInfoData.find(
        i => i.name.trim() === behaviour
      )
      const oldData = prevAssessment?.behaviourInfoData.find(
        i => i.name.trim() === behaviour
      )
      setCurrentBehaviour(newData)
      setPrevBehaviour(oldData)

      // Get the display data for the score change bar
      const constScoreChangeBarData = scoreField => {
        const newDisplayData = getDisplayScore(
          oldData ? oldData[scoreField] : 0,
          newData ? newData[scoreField] : 0,
          2
        )
        newDisplayData.oldTimeStamp = prevAssessment?.ratingTimeStamp
        newDisplayData.newTimeStamp = currentAssessment?.ratingTimeStamp
        return newDisplayData
      }

      const newDisplayData = {
        self: {
          name: 'self',
          displayScores: constScoreChangeBarData('avgSelfRating')
        }
      }
      const enableOthersAssessment =
        currentAssessment?.goalData?.enableOthersAssessment
      if (enableOthersAssessment) {
        newDisplayData.others = {
          name: 'others',
          displayScores: constScoreChangeBarData('avgOthersRating')
        }
      }
      setDisplayData(newDisplayData)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [behaviour])

  return (
    <ColStack spacing={8}>
      <Divider
        sx={{
          borderColor: colors.Grey100,
          width: '100%'
        }}
      />

      <Typography variant='eyebrow'>
        {t('assessmentTimeline.overallAverageRating')}
      </Typography>

      <ColStack spacing={4}>
        <RowStack spacing={16}>
          {Object.keys(displayData).map(key => (
            <AverageComparisionCard key={uuidv4()} {...displayData[key]} />
          ))}
        </RowStack>
      </ColStack>

      <Typography variant='h4'>{t('assessmentBehaviour.questions')}</Typography>

      {currentBehaviour?.questions.map((value, index) => {
        const selfScoreField = 'selfScore'
        const newSelfScore = value[selfScoreField]
        const oldSelfScore = prevBehaviour?.questions[index][selfScoreField]

        const otherScoreField = 'othersScore'
        const newOtherScore = value[otherScoreField]
        const oldOtherScore = prevBehaviour?.questions[index][otherScoreField]

        return (
          <ColStack key={`question-comp-${index}`} spacing={8}>
            <Typography
              fontWeight={500}
              lineHeight={'150%'}
              color='textSecondary'>
              {index + 1}. {value.selfQuestion}
            </Typography>

            <RowStack spacing={16}>
              <ColStack spacing={4}>
                <ScoreBarWrapper
                  score={newSelfScore}
                  date={displayData?.self?.displayScores?.newTimeStamp}
                  backgroundColor={alpha(colors.Teal400, 0.15)}
                />

                {oldSelfScore >= 0 && (
                  <ScoreBarWrapper
                    score={oldSelfScore}
                    date={displayData?.self?.displayScores?.oldTimeStamp}
                  />
                )}
              </ColStack>

              {displayData.others && (
                <ColStack spacing={4}>
                  <ScoreBarWrapper
                    score={newOtherScore}
                    date={displayData?.others?.displayScores?.newTimeStamp}
                    selfScore={false}
                    backgroundColor={alpha(colors.MidnightTeal300, 0.15)}
                  />

                  {oldOtherScore >= 0 && (
                    <ScoreBarWrapper
                      score={oldOtherScore}
                      date={displayData?.others?.displayScores?.oldTimeStamp}
                      selfScore={false}
                    />
                  )}
                </ColStack>
              )}
            </RowStack>
          </ColStack>
        )
      })}
    </ColStack>
  )
}

export default BehaviourComparison
